<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <!-- Header -->
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center gap-2">
              <b-button
                variant="outline"
                size="sm"
                @click="handleBack"
                v-b-tooltip.hover
                title="ย้อนกลับ"
              >
                <i
                  class="fas fa-chevron-left"
                  style="font-size: 20px"
                ></i>
              </b-button>
              <h4 class="mb-0">ใบขอซื้อ</h4>
              <PrStatusBadge
                v-if="pr"
                :status="pr.status"
              />
            </div>
            <div class="d-flex gap-4">
              <b-button
                v-if="pr && isPrEditable"
                class="edit-btn"
                variant="outline-primary"
                size="sm"
                @click="openEditModal"
              >
                แก้ไขรายการ
              </b-button>
              <b-button
                v-if="pr && isPrApprove"
                class="approve-btn"
                variant="primary"
                size="sm"
                @click="showApproveConfirm = true"
              >
                อนุมัติ
              </b-button>
            </div>
          </div>

          <!-- Two-column layout -->
          <div
            v-if="pr"
            class="pr-detail-layout"
          >
            <!-- Left: metadata -->
            <div class="pr-detail-info">
              <div class="info-row">
                <span class="info-label">เลขที่เอกสาร:</span>
                <span class="info-value">{{ pr.no || "-" }}</span>
              </div>
              <div
                class="info-row"
              >
                <span class="info-label">รหัสใบวางแผนความต้องการวัสดุ:</span>
                <span class="info-value">{{ pr.mrpNo || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">วันที่ทำรายการ:</span>
                <span class="info-value">{{ formatDate(pr.creationDt) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ชื่อสาขา:</span>
                <span class="info-value">{{ pr.branchName || "-" }}</span>
              </div>
            </div>

            <!-- Right: product table -->
            <div class="pr-detail-table">
              <b-table
                id="prDetailProductTable"
                hover
                small
                responsive
                head-variant="light"
                :items="prProductsWithImage"
                :fields="productFields"
              >
                <template v-slot:cell(index)="row"
                  >{{ row.index + 1 }}.</template
                >
                <template v-slot:cell(image)="row">
                  <ProductImage :src="row.item.imageUrl" />
                </template>
                <template v-slot:cell(productCode)="row">
                  {{ row.item.no || row.item.productCode || "-" }}
                </template>
                <template v-slot:cell(productName)="row">
                  {{ row.item.description || "-" }}
                </template>
                <template v-slot:cell(quantity)="row">
                  {{ row.item.quantity }}
                </template>
                <template v-slot:cell(uom)="row">
                  {{
                    (row.item.uom && row.item.uom.description) ||
                    row.item.uomName ||
                    "-"
                  }}
                </template>
              </b-table>
            </div>
          </div>

          <!-- Related POs section -->
          <div
            v-if="pr"
            class="mt-4"
          >
            <h5 class="related-po-title">รายการใบสั่งซื้อที่เกี่ยวข้อง</h5>
            <b-table
              id="prDetailPoTable"
              hover
              small
              sort-icon-left
              head-variant="light"
              sort-by="creationDt"
              :sort-desc="true"
              :items="pr.purchaseOrders || []"
              :fields="poFields"
            >
              <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
              <template v-slot:cell(status)="row">
                <PoStatusBadge :status="row.item.status" />
              </template>
              <template v-slot:cell(creationDt)="row">
                {{ formatDate(row.item.creationDt || row.item.createdAt) }}
              </template>
              <template v-slot:cell(actions)="row">
                <ActionButtonGroup
                  :item="row.item"
                  :show-delete="false"
                  :show-edit="false"
                  :show-info="true"
                  infoTooltip="ดูข้อมูลใบสั่งซื้อ"
                  @info="$router.push({ name: 'PurchaseOrderDetail', params: { clinicUrl: $route.params.clinicUrl, branchUrl: $route.params.branchUrl, id: row.item.id } })"
                />
              </template>
            </b-table>
          </div>
        </div>
      </div>

      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>

      <b-modal
        v-model="showApproveConfirm"
        title="อนุมัติใบขอซื้อ"
        centered
        no-close-on-backdrop
      >
        <p>
          เมื่ออนุมัติแล้ว ใบขอซื้อจะถูกส่งเข้าระบบ ERP และไม่สามารถแก้ไขได้<br />คุณต้องการอนุมัติใบขอซื้อนี้ใช่หรือไม่?
        </p>
        <template v-slot:modal-footer>
          <b-button
            class="cancel-btn"
            variant="outline-secondary"
            @click="showApproveConfirm = false"
            >ย้อนกลับ</b-button
          >
          <b-button
            class="confirm-btn"
            variant="primary"
            @click="approvePr"
            >ยืนยันอนุมัติ</b-button
          >
        </template>
      </b-modal>

      <CreateOrUpdatePurchaseReuqestModel
        ref="createOrUpdatePurchaseReuqestModel"
        @success="onEditSuccess"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Dialog from "@/components/modal/Dialog.vue";
import Loading from "@/components/Loading";
import PrStatusBadge from "@/components/badge/PrStatusBadge.vue";
import PoStatusBadge from "@/components/badge/PoStatusBadge.vue";
import ActionButtonGroup from "@/components/table/stock/button/ActionButtons.vue";
import CreateOrUpdatePurchaseReuqestModel from "@/components/modal/ProductAndStock/PurchaseReuqest/CreateOrUpdatePurchaseReuqestModel.vue";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";
import { mapActions, mapGetters } from "vuex";
import { PR_STATUS } from "@/constants/productStock";
import { ROLES } from "@/constants/user";
import { getErrorMessage } from "@/constants/errorMessages";

export default {
  name: "PurchaseRequestDetail",
  components: {
    Dialog,
    Loading,
    PrStatusBadge,
    PoStatusBadge,
    CreateOrUpdatePurchaseReuqestModel,
    ActionButtonGroup,
    ProductImage,
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fromRoute = from;
    });
  },
  data() {
    return {
      ROLES,
      isLoading: false,
      pr: null,
      fromRoute: null,
      showApproveConfirm: false,
      productFields: [
        { key: "index", label: "", thStyle: { width: "40px" } },
        { key: "image", label: "", thStyle: { width: "50px", minWidth: "50px" } },
        { key: "productCode", label: "รหัส", thStyle: { minWidth: "100px" } },
        {
          key: "productName",
          label: "ชื่อสินค้า",
          thStyle: { minWidth: "160px" },
        },
        {
          key: "quantity",
          label: "จำนวนสินค้า",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
        { key: "uom", label: "หน่วยสินค้า", thStyle: { minWidth: "100px" } },
      ],
      poFields: [
        {
          key: "index",
          label: "",
          thStyle: { width: "6%" },
          tdStyle: { width: "5%" },
        },
        {
          key: "status",
          label: "สถานะ",
          sortable: true,
          thStyle: { width: "20%" },
        },
        {
          key: "no",
          label: "รหัสใบสั่งซื้อ",
          sortable: true,
          thStyle: { width: "60%" },
        },
        {
          key: "creationDt",
          label: "วันที่สร้าง",
          sortable: true,
          thStyle: { width: "20%" },
        },
        {
          key: "actions",
          label: "",
          sortable: false,
          thStyle: { width: "10%" },
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getProductList: "moduleProductInventory/getProductList",
      isAccessMenu: "moduleUser/getIsAccessMenu",
    }),
    isStockManagerOrStaff() {
      return this.isAccessMenu([
        ROLES.ROLE_BRANCH_STOCK_MANAGER,
        ROLES.ROLE_STOCK_STAFF,
      ]);
    },
    isStockManager() {
      return this.isAccessMenu([ROLES.ROLE_BRANCH_STOCK_MANAGER]);
    },
    isPrEditable() {
      return (
        this.pr?.status === PR_STATUS.DRAFT && this.isStockManagerOrStaff
      );
    },
    isPrApprove() {
      return this.pr?.status === PR_STATUS.DRAFT && this.isStockManager;
    },
    prProductsWithImage() {
      if (!this.pr?.products) return [];
      return this.pr.products.map(p => ({
        ...p,
        imageUrl:
          this.getProductList.find(
            pl =>
              pl.productId === p.productId ||
              pl.productId === p.id ||
              pl.productCode === p.no ||
              pl.productCode === p.productCode
          )?.imageUrl || null,
      }));
    },
  },
  async mounted() {
    this.isLoading = true;
    try {
      const { clinicUrl, branchUrl, prId } = this.$route.params;
      this.pr = await this.fetchPrById({ clinicUrl, branchUrl, id: prId });
      if (!this.pr) {
        this.goBack();
        return;
      }
    } catch (err) {
      console.error("fetchPrById failed", err);
      this.goBack();
      return;
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    ...mapActions({
      fetchPrById: "modulePurchaseRequest/fetchPrById",
      fetchProductList: "moduleProductInventory/fetchProductList",
      fetchPurchaseRequests: "modulePurchaseRequest/fetchPurchaseRequests",
      approvePurchaseRequest: "modulePurchaseRequest/approvePurchaseRequest",
    }),
    formatDate(date) {
      if (!date) return "-";
      return moment(date).locale("th").format("DD MMM YY");
    },
    handleBack() {
      if (this.fromRoute?.name) {
        this.$router.go(-1);
      } else {
        this.goBack();
      }
    },
    goBack() {
      this.$router.push({
        name: "PurchaseRequest",
        params: {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        },
      });
    },
    poStatusStyle(_status) {
      return {
        backgroundColor: "#E9ECEF",
        color: "#3E495B",
        fontWeight: "400",
        fontSize: "14px",
        borderRadius: "20px",
        padding: "0.35em 0.65em",
      };
    },
    async openEditModal() {
      this.isLoading = true;
      try {
        const { clinicUrl, branchUrl } = this.$route.params;
        if (!this.getProductList?.length) {
          await this.fetchProductList({ clinicUrl, branchUrl });
        }
        this.$refs.createOrUpdatePurchaseReuqestModel.show({
          prId: this.$route.params.prId,
          mrpNo: this.pr.mrpNo,
          mrpId: this.pr.mrpId,
          products: this.pr.products || [],
          productList: this.getProductList,
          type: "UPDATE",
        });
      } catch (err) {
        console.error("openEditModal failed", err);
        this.$refs.Dialog.showToast("danger", "โหลดข้อมูลไม่สำเร็จ");
      } finally {
        this.isLoading = false;
      }
    },
    async approvePr() {
      this.showApproveConfirm = false;
      this.isLoading = true;
      try {
        const { clinicUrl, branchUrl, prId } = this.$route.params;
        await this.approvePurchaseRequest({ clinicUrl, branchUrl, prId });
        this.pr = await this.fetchPrById({ clinicUrl, branchUrl, id: prId });
        this.$refs.Dialog.showToast(
          "success",
          "อนุมัติใบขอซื้อสำเร็จ ระบบส่งข้อมูลให้ ERP แล้ว"
        );
      } catch (err) {
        console.error("approvePr error =>", err);
        this.$refs.Dialog.showToast("danger", getErrorMessage(err));
      } finally {
        this.isLoading = false;
      }
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    async onEditSuccess() {
      this.isLoading = true;
      try {
        const { clinicUrl, branchUrl, prId } = this.$route.params;
        this.pr = await this.fetchPrById({ clinicUrl, branchUrl, id: prId });
      } catch (err) {
        console.error("reload pr failed", err);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
h4 {
  margin: 0;
}

.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}

.pr-detail-layout {
  display: flex;
  gap: 24px;
  border: 1px solid #dee2e6;
  padding: 20px;
}

.pr-detail-info {
  flex: 0 0 30%;
  min-width: 0;
  border-right: 1px solid #dee2e6;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pr-detail-table {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.border-bottom {
  border-bottom: 1px solid #dee2e6;
}

.info-row {
  line-height: 1.6;
  display: flex;
}

.info-label {
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  flex-shrink: 0;
}

.info-value {
  margin-left: 4px;
  word-break: break-word;
  min-width: 0;
  flex: 1;
}

.product-img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}

.product-img-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #f0f0f0;
  border-radius: 4px;
  color: #aaa;
}

.related-po-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
}

::v-deep #prDetailProductTable thead th,
::v-deep #prDetailProductTable tbody td {
  vertical-align: middle !important;
}

::v-deep #prDetailProductTable tbody tr:last-child td {
  border-bottom: 1px solid #dee2e6 !important;
}

::v-deep #prDetailPoTable thead th,
::v-deep #prDetailPoTable tbody td {
  vertical-align: middle !important;
}

::v-deep #prDetailPoTable td {
  padding: 16px 24px !important;
  word-break: break-word !important;
  white-space: normal !important;
}

::v-deep #prDetailPoTable th {
  padding: 16px 24px !important;
  word-break: break-word !important;
  white-space: normal !important;
}

.edit-btn {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #5299d6;
  color: #5299d6;
  background-color: transparent;
}
.edit-btn:hover {
  background-color: #5299d6;
  color: #fff;
}

.approve-btn {
  padding: 8px 50px;
  font-size: 14px;
  font-weight: 600;
}

@media (max-width: 768px) {
  .pr-detail-layout {
    flex-direction: column;
  }

  .pr-detail-info {
    flex: none;
    border-right: none;
    padding-right: 0;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 16px;
  }
}

.cancel-btn {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border-color: #acb5bd;
  color: #212529;
}
.cancel-btn:hover {
  background-color: #acb5bd;
  color: #fff;
}

.confirm-btn {
  padding: 8px 50px;
  font-size: 14px;
  font-weight: 600;
}
</style>
